import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  useSidebar,
} from "@nfp-tracker/components";
import { NavLink, useLocation } from "react-router-dom";
import { useInstructorClient } from "../components/InstructorClientContext";
import { ROUTES } from "../constants/routes";
import {
  readOnboardingCompleteCache,
  useOnboardingChecklist,
} from "../hooks/useOnboardingChecklist";
import { NewBadge } from "../components/NewBadge";
import { useActorRole } from "../hooks/useOwnerContext";
import { useCurrentUserWhatsNew } from "../hooks/useCurrentUserWhatsNew";
import { getNavItems, type AppNavItem } from "./navigation";

const isRouteActive = (pathname: string, route: string) =>
  pathname === route || pathname.startsWith(`${route}/`);

export const AppSidebarNav = () => {
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const { loading, allComplete } = useOnboardingChecklist();
  const { activeClient } = useInstructorClient();
  const whatsNew = useCurrentUserWhatsNew();

  // Only surface the Get Started entry once we know the user hasn't finished.
  // While the live state loads, fall back to the cached flag so returning users
  // see the correct state immediately (no show-then-hide flash).
  const showGetStarted = loading
    ? readOnboardingCompleteCache() === false
    : !allComplete;

  // The signed-in account's own role (never the open client's), from the app-wide owner context
  // or, on a cold load, this same user's persisted hint — so it doesn't flip while clients are
  // opened and closed, and costs no extra round trip.
  const { role, isResolved: isRoleResolved } = useActorRole();
  const isInstructor = role === "instructor";

  const navItems = getNavItems({
    isInstructor,
    activeClientId: isInstructor ? activeClient?.clientUserId ?? null : null,
  }).filter((item) => {
    if (item.to === ROUTES.gettingStarted) return showGetStarted;
    return true;
  });

  // The client-chart tabs sit under the active client's name so the sidebar reads as "the app,
  // currently pointed at this one client". No other client's name ever appears here.
  const clientTabItems = navItems.filter((item) => item.clientTab);
  const firstClientTabIndex = navItems.findIndex((item) => item.clientTab);
  const beforeClientItems =
    firstClientTabIndex === -1 ? navItems : navItems.slice(0, firstClientTabIndex);
  const afterClientItems =
    firstClientTabIndex === -1
      ? []
      : navItems.slice(firstClientTabIndex).filter((item) => !item.clientTab);

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const renderItem = (item: AppNavItem) => {
    const Icon = item.icon;
    const isActive = isRouteActive(pathname, item.to);
    const showNew =
      item.to === ROUTES.gettingStarted ? whatsNew.hasNew : whatsNew.isPathNew(item.to);

    return (
      <SidebarMenuItem key={item.to}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={item.label}
          className={
            item.highlight
              ? "my-1 h-12 bg-gradient-to-r from-primary to-fuchsia-500 text-primary-foreground shadow-md ring-1 ring-primary/40 transition-all hover:from-primary hover:to-fuchsia-500 hover:text-primary-foreground hover:opacity-95 hover:shadow-lg active:from-primary active:to-fuchsia-500 data-[active=true]:from-primary data-[active=true]:to-fuchsia-500 data-[active=true]:text-primary-foreground"
              : "h-10"
          }
        >
          <NavLink to={item.to} onClick={handleNavClick}>
            {/* The wrapper hides the icon from the menu button's `[&>svg]:size-4`, so size it here. */}
            <span className="relative flex shrink-0">
              <Icon className={`h-4 w-4 ${item.highlight ? "animate-pulse" : ""}`} />
              {showNew && (
                <NewBadge
                  variant="dot"
                  className={`absolute -right-1 -top-1 ${item.highlight ? "bg-primary-foreground" : ""}`}
                />
              )}
            </span>
            <div className="min-w-0 group-data-[collapsible=icon]:hidden">
              <p
                className={`truncate leading-tight ${
                  item.highlight ? "font-bold" : ""
                }`}
              >
                {item.label}
              </p>
              <p
                className={`truncate text-xs ${
                  item.highlight
                    ? "text-primary-foreground/80"
                    : "opacity-70"
                }`}
              >
                {item.description}
              </p>
            </div>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  const renderGroup = (label: string, items: AppNavItem[]) =>
    items.length ? (
      <SidebarGroup>
        <SidebarGroupLabel className="truncate" title={label}>
          {label}
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>{items.map(renderItem)}</SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    ) : null;

  // Until the role is known, show neither nav: defaulting to the personal items is what made
  // instructors see Observations / Bulk Entry / Get Started flash in before their own nav.
  if (!isRoleResolved) {
    return (
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu aria-busy="true" aria-label="Loading navigation" data-testid="nav-skeleton">
              {Array.from({ length: 4 }).map((_, index) => (
                <SidebarMenuItem key={index}>
                  <SidebarMenuSkeleton showIcon className="h-10" />
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    );
  }

  return (
    <SidebarContent>
      {renderGroup("Navigation", beforeClientItems)}
      {clientTabItems.length && activeClient
        ? renderGroup(activeClient.displayName, clientTabItems)
        : null}
      {renderGroup("Account", afterClientItems)}
    </SidebarContent>
  );
};
